import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  TableSettings,
  TableSpacing,
} from 'src/app/components/pagination/pagination.component';
import { ErrorService } from 'src/app/services/error.service';
import {
  DesktopLicenseQueryItem,
  LicensesService,
} from 'src/app/services/licenses.service';
import { Product, ProductsService } from 'src/app/services/products.service';

type SortField = 'account' | 'company' | 'expiration';
type SortDirection = 'ASC' | 'DESC';

@Component({
  selector: 'app-licenses-list',
  templateUrl: './licenses-list.component.html',
  styleUrls: ['./licenses-list.component.scss'],
})
export class LicensesListComponent implements OnInit {
  @ViewChild('productSearchInput')
  productSearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('productDropdown')
  productDropdown?: ElementRef<HTMLElement>;

  products: Product[] = [];
  loadingProducts = false;
  errorFetchingProducts = '';
  productSearch = '';
  productDropdownOpen = false;
  selectedProductIds = new Set<string>();

  fromDate = '';
  toDate = '';

  licenses: DesktopLicenseQueryItem[] = [];
  queried = false;
  querying = false;
  errorQuerying = '';

  sortField: SortField = 'expiration';
  sortDirection: SortDirection = 'ASC';

  pageSize = 25;
  currentPage = 1;
  tableSpacing: TableSpacing = 'normal';

  constructor(
    private productsService: ProductsService,
    private licensesService: LicensesService,
    private errorService: ErrorService,
    private router: Router
  ) {}

  ngOnInit() {
    const now = new Date();
    this.fromDate = this.formatDateInput(
      new Date(now.getFullYear(), now.getMonth(), 1)
    );
    this.toDate = this.formatDateInput(
      new Date(now.getFullYear(), now.getMonth() + 1, 0)
    );
    this.loadProducts();
  }

  get filteredProducts() {
    const products = [...this.products].sort((a, b) =>
      a.code.localeCompare(b.code)
    );
    if (!this.productSearch || this.productSearch === '') {
      return products;
    }

    const term = this.productSearch.trim().toLowerCase();
    return products.filter(
      (p) =>
        p.code.toLowerCase().includes(term) ||
        p.title.toLowerCase().includes(term) ||
        p.variance_code.toLowerCase().includes(term)
    );
  }

  get productDropdownLabel() {
    if (this.selectedProductIds.size === 0) {
      return 'All products';
    }
    if (this.selectedProductIds.size === 1) {
      const selected = this.products.find((p) =>
        this.selectedProductIds.has(p.id)
      );
      if (selected) {
        return `${selected.code} - ${selected.title} (${selected.variance_code})`;
      }
    }
    return `${this.selectedProductIds.size} products selected`;
  }

  get canQuery() {
    if (this.fromDate && this.toDate && this.fromDate > this.toDate) {
      return false;
    }
    return (
      this.selectedProductIds.size > 0 || (!!this.fromDate && !!this.toDate)
    );
  }

  get sortedLicenses() {
    const licenses = [...this.licenses];
    const direction = this.sortDirection === 'ASC' ? 1 : -1;
    licenses.sort((a, b) => {
      let comparison = 0;
      if (this.sortField === 'account') {
        comparison = a.tenant.account_number.localeCompare(
          b.tenant.account_number
        );
      } else if (this.sortField === 'company') {
        comparison = a.tenant.company.localeCompare(b.tenant.company);
      } else {
        comparison =
          this.dateValue(a.expiration) - this.dateValue(b.expiration);
      }
      return comparison * direction;
    });
    return licenses;
  }

  get totalPages() {
    return Math.max(1, Math.ceil(this.sortedLicenses.length / this.pageSize));
  }

  get pagedLicenses() {
    const offset = (this.currentPage - 1) * this.pageSize;
    return this.sortedLicenses.slice(offset, offset + this.pageSize);
  }

  loadProducts() {
    this.loadingProducts = true;
    this.errorFetchingProducts = '';
    this.productsService.getAllProducts().subscribe({
      next: (res) => {
        this.loadingProducts = false;
        this.products = res.products ?? [];
      },
      error: (err) => {
        this.loadingProducts = false;
        this.errorFetchingProducts = this.errorService.getErrorMessage(err);
      },
    });
  }

  isProductSelected(productId: string) {
    return this.selectedProductIds.has(productId);
  }

  toggleProduct(productId: string) {
    if (this.selectedProductIds.has(productId)) {
      this.selectedProductIds.delete(productId);
    } else {
      this.selectedProductIds.add(productId);
    }
  }

  toggleProductDropdown() {
    this.productDropdownOpen = !this.productDropdownOpen;
    if (this.productDropdownOpen) {
      this.focusProductSearchInput();
    }
  }

  focusProductSearchInput() {
    setTimeout(() => {
      this.productSearchInput?.nativeElement.focus();
    }, 0);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.productDropdownOpen) {
      return;
    }
    const target = event.target as Node | null;
    if (target && this.productDropdown?.nativeElement.contains(target)) {
      return;
    }
    this.productDropdownOpen = false;
  }

  selectFilteredProducts() {
    for (const product of this.filteredProducts) {
      this.selectedProductIds.add(product.id);
    }
  }

  clearSelectedProducts() {
    this.selectedProductIds.clear();
  }

  queryLicenses() {
    if (!this.canQuery || this.querying) {
      return;
    }

    this.querying = true;
    this.errorQuerying = '';
    this.currentPage = 1;

    this.licensesService
      .queryDesktopLicenses({
        product_ids: Array.from(this.selectedProductIds),
        from_date: this.fromDate || undefined,
        to_date: this.toDate || undefined,
      })
      .subscribe({
        next: (res) => {
          this.querying = false;
          this.queried = true;
          this.licenses = res.desktop_licenses ?? [];
        },
        error: (err) => {
          this.querying = false;
          this.queried = true;
          this.licenses = [];
          this.errorQuerying = this.errorService.getErrorMessage(err);
        },
      });
  }

  sortBy(field: SortField) {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'ASC' ? 'DESC' : 'ASC';
    } else {
      this.sortField = field;
      this.sortDirection = 'ASC';
    }
    this.currentPage = 1;
  }

  handleSettingsChanged(settings: TableSettings) {
    this.pageSize = Number(settings.pageSize);
    this.tableSpacing = settings.spacing;
    this.currentPage = 1;
  }

  goToTenant(tenantId: string) {
    this.router.navigate(['/admin/tenant', tenantId]);
  }

  goToLicense(licenseId: string) {
    this.router.navigate(['/admin/edit-license', licenseId]);
  }

  exportCsv() {
    const rows = [
      [
        'account_number',
        'company',
        'product_code',
        'product_title',
        'duration',
        'unique_id',
        'start_date',
        'expiration',
        'active',
        'will_renew',
        'site_license',
        'tenant_id',
        'license_id',
      ],
      ...this.sortedLicenses.map((license) => [
        license.tenant.account_number,
        license.tenant.company,
        license.product.code,
        license.product.title,
        license.product_variant.display_duration || '',
        license.unique_id ?? '',
        this.formatDate(license.start_date),
        this.formatDate(license.expiration),
        String(license.active),
        String(license.will_renew),
        String(license.site_license),
        license.tenant.id,
        license.id,
      ]),
    ];
    const csv = rows
      .map((row) => row.map((cell) => this.csvCell(cell)).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const today = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `desktop-licenses-${today}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  private dateValue(value?: string) {
    if (!value) {
      return 0;
    }
    return new Date(value).getTime();
  }

  formatDate(value?: string) {
    if (!value) {
      return '';
    }
    return value.slice(0, 10);
  }

  private formatDateInput(date: Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private csvCell(value: string) {
    if (/^[=+\-@\t\r]/.test(value)) {
      value = `'${value}`;
    }
    if (/[",\r\n]/.test(value)) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  }
}
